import type { ButtonHTMLAttributes } from 'react';

export interface ToggleProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange'> {
  enabled: boolean;
  onChange: (enabled: boolean) => void;
  label?: string;
  disabled?: boolean;
}

export function Toggle({
  enabled,
  onChange,
  label,
  disabled = false,
  className = '',
  ...props
}: ToggleProps) {
  const handleClick = () => {
    if (!disabled) {
      onChange(!enabled);
    }
  };

  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      aria-label={label}
      onClick={handleClick}
      disabled={disabled}
      className={`relative inline-flex items-center ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${className}`}
      {...props}
    >
      <div className="h-7 w-[62px] relative">
        {enabled ? (
          <>
            {/* ON state - Blue gradient */}
            <div className="absolute bg-[#0350a0] border-[3px] border-black border-solid inset-0 rounded-[14px]" />
            <div className="absolute bg-gradient-to-t bottom-[8.33%] from-[#0875d2] from-[1.143%] left-0 right-0 rounded-[14px] to-[#41b3fc] to-[100.14%] top-0 via-[#0485e8] via-[49.653%]" />
            {/* Handle shadow/background */}
            <div className="absolute bg-[#f2f2f2] bottom-[8.33%] left-[58.49%] right-0 rounded-[14px] top-0" />
            {/* Handle */}
            <div className="absolute bg-white inset-[6.25%_2.83%_14.58%_61.32%] rounded-[14px] transition-all duration-200" />
          </>
        ) : (
          <>
            {/* OFF state - Gray */}
            <div className="absolute bg-[#333333] border-[3px] border-black border-solid inset-0 rounded-[14px]" />
            <div className="absolute bottom-[8.33%] left-0 right-0 rounded-[14px] top-0" />
            {/* Handle shadow/background */}
            <div className="absolute bg-[#f2f2f2] bottom-[8.33%] left-0 right-[58.49%] rounded-[14px] top-0" />
            {/* Handle */}
            <div className="absolute bg-white inset-[6.25%_61.32%_14.58%_2.83%] rounded-[14px] transition-all duration-200" />
          </>
        )}
      </div>
      {label && <span className="ml-3 text-white font-medium">{label}</span>}
    </button>
  );
}
